// 使用示例：
// const newFilename = generateFilename('test.jpg');
// console.log(newFilename); // 输出类似: 1704067200000202401011230001234.jpg

import { uploadFile } from '@/services/common';
import {
  httpImageProcessQueryPatientTask,
  httpImageProcessQueryTaskDetails,
  httpImageRecognitionCreate,
  httpImageRecognitionFind,
  httpImageUploadFail,
} from '@/services/imageProcess';
import {
  httpCosCredentials,
  httpGetImageByMd5,
  httpImageClassify,
  httpImageProcessingFail,
  httpSaveImageClassify,
} from '@/services/patient';
import { isCOSKeyValid } from '@/store/imageRecog';
import { base64ToArrayBuffer, isDev } from '@/utils';
import { getLocalStorage, setLocalStorage } from '@/utils/cookies';
import { useFileMD5 } from '@/utils/fileUtils';
import { CompressFilesProps } from '@/utils/picControler/ImageBase';
import { decrypt } from '@/utils/sm-crypto';
import { message } from '@sinohealth/butterfly-ui-components';
// @ts-ignore
import COS from 'cos-js-sdk-v5';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * 生成文件名称
 * @param {string} filename 原始文件名
 * @returns {string} 生成的新文件名
 */
export const generateFilename = (filename: string) => {
  const timestamp = Date.now();
  const timeId = generateIdTime20();
  const extension = getExtension(filename);

  return `${timestamp}${timeId}.${extension}`;
};

/**
 * 生成20位时间ID (模拟 IdUtils.generateIdTime20())
 * @returns {string} 20位时间ID
 */
const generateIdTime20 = () => {
  // 生成一个基于时间的20位ID
  // 这里使用当前时间 + 随机数的方式模拟
  const now = new Date();
  const timeStr =
    now.getFullYear().toString() +
    (now.getMonth() + 1).toString().padStart(2, '0') +
    now.getDate().toString().padStart(2, '0') +
    now.getHours().toString().padStart(2, '0') +
    now.getMinutes().toString().padStart(2, '0') +
    now.getSeconds().toString().padStart(2, '0');

  // 添加毫秒和随机数确保唯一性，总长度20位
  const ms = now.getMilliseconds().toString().padStart(3, '0');
  const random = Math.floor(Math.random() * 100)
    .toString()
    .padStart(2, '0');

  return (timeStr + ms + random).substring(0, 20);
};

/**
 * 获取文件扩展名
 * @param {string} filename 文件名
 * @returns {string} 文件扩展名
 */
const getExtension = (filename: string) => {
  if (!filename) {
    throw new Error('filename cannot be null or undefined');
  }

  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return ''; // 没有扩展名
  }

  return filename.substring(lastDotIndex + 1);
};

/**
 * 处理COS Location，返回以 /var 或 /video 开头的路径
 * @param {string} location COS返回的Location字符串
 * @returns {string} 以 /var 或 /video 开头的路径，否则返回空字符串
 */
export const processCOSLocation = (location: string): string => {
  if (!location) return '';

  // 匹配以 /var/ 或 /video/ 开头的路径
  const locationMatch = location.match(/(\/(var|video)\/.+)$/);
  if (locationMatch) {
    return locationMatch[1]; // 返回以 /var 或 /video 开头的路径
  }

  // 没有匹配到则返回空字符串
  return '';
};

export const TextinError: any = {
  40101: 'x-ti-app-id 或 x-ti-secret-code 为空',
  40102: 'x-ti-app-id 或 x-ti-secret-code 无效，验证失败',
  40103: '客户端IP不在白名单',
  40003: '余额不足，请充值后再使用',
  40004: '参数错误，请查看技术文档，检查传参',
  40007: '机器人不存在或未发布',
  40008: '机器人未开通，请至市场开通后重试',
  40301: '文件类型不支持，接口会返回实际检测到的文件类型，如“当前文件类型为.gif”',
  40302: '上传文件大小不符，文件大小不超过 50M',
  40303: '文件类型不支持',
  40304: '图片尺寸不符，图像宽高须介于 20 和 10000（像素）之间',
  40305: '识别文件未上传',
  40400: '无效的请求链接，请检查链接是否正确',
  30203: '基础服务故障，请稍后重试',
  500: '服务器内部错误',
};

export const UploadStatusTip = {
  waiting: '等待上传中...',
  uploading: '图片上传中...',
  scanning: '图片扫描中...',
  desensitization: '身份脱敏中...',
  processing: '分析整理中...',
  success: '上传成功',
  fail: '上传失败',
};

export type UploadStatus =
  | 'waiting' // 等待上传
  | 'uploading' // 上传中
  | 'scanning' // 扫描中
  | 'desensitization' // 身份脱敏中
  | 'processing' // 处理中
  | 'success' // 成功
  | 'fail'; // 失败

export interface ProcessingFilesProps extends CompressFilesProps {
  uploadStatus?: UploadStatus;
}

/**
 * 图片智能识别
 * @param {any} params
 * @returns {any}
 *
 * 1. 用户选择文件 -> 需要过滤视频文件（视频按原有逻辑处理）
 * 2. 计算文件MD5 -> 根据MD5判断文件是否已存在 -> 如果存在，则不进行上传 -> 如果不存在，则进行上传
 * 3. 并发上传文件到COS -> 获取cos临时密钥，上传文件到COS
 * 4. 调用图像分类接口 -> 每上传成功5张，调用一次图像分类接口
 * 5. 调用去屏幕纹API -> 根据分类返回的数据，判断是否为胶片，如果不是胶片，则并发调用去屏幕纹API；如果是胶片，则不进行去屏幕纹处理
 * 6. 调用修边矫正API -> 根据去屏幕纹API返回的数据，并发调用修边矫正API
 * 7. 保存图像 -> 根据修边矫正API返回的数据 + md5，保存图像
 * 8. 轮询图片任务列表 -> 轮询图片任务列表，如果任务列表为空，则结束，callback
 * 9. 查询图片处理任务详情 -> 查询图片处理任务详情，如果任务详情为空，则结束，callback
 * 10.
 */
export const useImageRecognition = (params?: any) => {
  const { scene = 'multi', fileType = '', recordId, callback, canOcr } = params;
  const [param] = useSearchParams();
  const patientId: string = decrypt(
    param.get('patientId') || param.get('id') || params?.patientId || '',
  );

  // 上传文件
  const [uploadImageFiles, setUploadImageFiles] = useState<CompressFilesProps[]>([]);
  // 保存loading
  const [saveImageLoading, setSaveImageLoading] = useState(false);
  // 图片智能识别编号
  const [recognitTaskId, setRecognitTaskId] = useState('');
  // 处理中的文件（包括等待上传和正在上传）
  const [processingImageFiles, setProcessingImageFiles] = useState<ProcessingFilesProps[]>([]);
  // 患者图片处理任务列表
  const [patientImageTasks, setPatientImageTasks] = useState<any[]>([]);

  // 临时key
  const tempCosKeyRef = useRef<any>(null);
  // COS实例
  const cosInstanceRef = useRef<any>(null);
  // 轮询图片任务列表
  const intervalRef = useRef<any>(null);
  // 图片任务列表
  const taskIdsRef = useRef<any>([]);
  // 存储recognitTaskId的ref，确保在异步操作中能正确获取
  const recognitTaskIdRef = useRef<string>('');

  // 计算文件MD5
  const { calculateFileMD5 } = useFileMD5();

  useEffect(() => {
    tempCosKeyRef.current = getLocalStorage('cosTempKey');

    handleInitCos();
  }, []);

  useEffect(() => {
    if (processingImageFiles.length === 0 && patientImageTasks.length === 0) {
      canOcr?.(true);
    } else {
      canOcr?.(false);
    }
  }, [processingImageFiles, patientImageTasks]);

  // 监听recognitTaskId变化，同步更新ref
  useEffect(() => {
    recognitTaskIdRef.current = recognitTaskId;
  }, [recognitTaskId]);

  /**
   * 初始化COS
   */
  const handleInitCos = useCallback(async () => {
    if (!isCOSKeyValid(tempCosKeyRef?.current)) {
      // console.log('cos临时密钥有效');
      const tempKey: any = await httpCosCredentials();
      setLocalStorage('cosTempKey', tempKey);
      tempCosKeyRef.current = tempKey;
    }
    if (cosInstanceRef?.current) return;
    try {
      const cos = new COS({
        getAuthorization: (options: any, fn: any) => {
          fn({
            TmpSecretId: tempCosKeyRef?.current?.accessKeyId,
            TmpSecretKey: tempCosKeyRef?.current?.accessKeySecret,
            SecurityToken: tempCosKeyRef?.current?.securityToken,
            StartTime: Math.floor(Date.now() / 1000), // 确保是10位整数时间戳
            ExpiredTime: tempCosKeyRef?.current?.expiration || 0,
          });
        },
      });

      cosInstanceRef.current = cos;
    } catch (error) {
      console.log(error);
    }
  }, []);

  /**
   * 上传文件到COS
   * @param {File} file 文件
   */
  const handleCosUploadFile = useCallback(async (file: CompressFilesProps, md5: string) => {
    if (!cosInstanceRef.current) {
      await handleInitCos();
    }
    // 生成文件名
    const cosKey = generateFilename(file.file.name);

    // 生成路径
    const path = file.file.type.includes('image') ? 'var' : 'video';

    // 将图片设置为上传中状态
    handleSetProcessingFiles({ ...file, uploadStatus: 'uploading' });

    // 上传文件
    return new Promise((resolve, reject) => {
      // 上传文件到COS
      cosInstanceRef.current?.putObject(
        {
          Bucket: tempCosKeyRef?.current?.bucketName, // 桶名
          Region: tempCosKeyRef?.current?.region, // 区域
          Key: `${path}/${cosKey}`, // 文件名
          SliceSize: 1024 * 1024 * 5, // 触发分块上传的阈值，超过5MB使用分块上传，小于5MB使用简单上传。可自行设置，非必须
          Body: file.file, // 文件
          onProgress: (data: any) => {
            // console.log(data);
          },
        },
        (err: any, data: any) => {
          if (err) {
            // console.log(JSON.stringify(err));
            // 移除图片记录
            message.error('cos上传失败，请稍后重试');
            try {
              httpImageUploadFail({
                patientId,
                fileMd5: md5,
                scene,
                failReason: `文件上传失败： %s ${JSON.stringify(err)}`,
              });
            } catch (error) {
              console.log('上传失败的埋点报错', err);
            }
            handleSetProcessingFiles({ ...file, new: false }, true);
            reject(err);
          } else {
            // console.log('上传成功', data);
            // 将图片设置为扫描中状态
            handleSetProcessingFiles({ ...file, new: false, uploadStatus: 'scanning' });
            // 处理Location，提取出路径后面的文件名
            const processedLocation = processCOSLocation(data.Location);
            resolve({
              processedLocation, // 添加处理后的Location
              originalLocation: data.Location, // 保留原始Location
              file, // 文件
              md5, // 文件MD5
            });
          }
        },
      );
    });
  }, []);

  /**
   * 图像分类
   * @param {File} file 文件
   */
  const httpImageClassifyReq = useCallback(
    async (files: { key: string; md5: string; file: CompressFilesProps }[]) => {
      try {
        // 调用图像分类接口
        const res: any = await httpImageClassify({
          patientId,
          scene,
          fileType,
          files: files?.map((el) => ({
            md5: el.md5,
            path: el.key,
          })),
        });
        // 如果分类结果不为空，则调用去屏幕纹API
        if (res && res.length > 0) {
          res.forEach((item: any) => {
            const file = files.find((el) => el.key === item.filePath);
            // 如果是胶片，则不进行去屏幕纹处理
            if (item?.isMedicalFilm) {
              // console.log('是胶片');
              httpImageProcessQueryTaskDetailsReq({ taskId: item.taskId, file: file?.file });
            } else {
              // console.log('不是胶片');
              // 如果文件存在，则获取COS URL
              cosInstanceRef.current.getObjectUrl(
                {
                  Bucket: tempCosKeyRef?.current?.bucketName, // 桶名
                  Region: tempCosKeyRef?.current?.region, // 区域
                  Key: item.filePath,
                },
                (err: any, data: any) => {
                  if (!file) return;
                  if (err) {
                    console.error('获取COS URL失败', err);
                    message.error('获取COS URL失败，请稍后重试');
                    handleSetProcessingFiles({ ...file.file }, true);
                    return;
                  }
                  // console.log('获取COS URL成功', data.Url);
                  // 调用去屏幕纹API
                  httpDemoireImageReq({
                    url: data.Url,
                    file: file.file,
                    md5: file.md5,
                    taskId: item.taskId,
                    add: true,
                  });
                },
              );
            }
          });
        } else {
          files.forEach((el) => {
            handleSetProcessingFiles({ ...el.file }, true);
          });
          message.error('图像分类处理失败，请稍后重试');
        }
      } catch (error) {
        console.log(error);
        message.error('图像分类处理失败，请稍后重试');
        files.forEach((el) => {
          handleSetProcessingFiles({ ...el.file }, true);
        });
      }
    },
    [],
  );

  /** 重新处理图片 */
  const stareDemoireImage = async (task: any) => {
    if (!recognitTaskIdRef.current) {
      await httpImageRecognitionFindReq();
    }
    const filePath = task.beforeFilePath;
    const file: any = {
      new: false,
      id: task.id,
    };
    cosInstanceRef.current.getObjectUrl(
      {
        Bucket: tempCosKeyRef?.current?.bucketName, // 桶名
        Region: tempCosKeyRef?.current?.region, // 区域
        Key: filePath,
      },
      (err: any, data: any) => {
        if (err) {
          console.error('获取COS URL失败', err);
          message.error('获取COS URL失败，请稍后重试');
        }
        console.log('获取COS URL成功', data.Url);
        // 调用去屏幕纹API
        httpDemoireImageReq({
          url: data.Url,
          file,
          add: false,
          taskId: task.id,
        });
      },
    );
  };

  /** 调用textin去屏幕纹API（通过URL） */
  const callTextinDemoireAPIByUrl = useCallback(
    async (textinData: { url: string; imageUrl: string | ArrayBuffer; taskId?: string }) => {
      try {
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', textinData.url);
          if (isDev) {
            xhr.setRequestHeader('x-ti-app-id', '9cb5799fbf4052ecabde600250d3381f');
            xhr.setRequestHeader('x-ti-secret-code', '569f80f4adcc16bfd03a40dc598de31b');
          }
          xhr.setRequestHeader(
            'Content-Type',
            textinData.imageUrl instanceof ArrayBuffer ? 'application/octet-stream' : 'text/plain',
          );
          xhr.send(textinData.imageUrl);

          xhr.onreadystatechange = function () {
            if (xhr.readyState === 4) {
              const response = xhr.response;
              try {
                const obj = JSON.parse(response);
                // console.log(obj);
                if (obj.code !== 200) {
                  httpImageProcessingFailReq({
                    taskId: textinData?.taskId,
                    failReason:
                      TextinError[obj.code] || obj?.msg || obj?.message || '合合图片处理失败',
                  });
                  message.error(
                    TextinError[obj.code] || obj?.msg || obj?.message || '合合图片处理失败',
                  );
                  reject();
                  return;
                }
                resolve((obj.result?.image as string) || obj.result);
              } catch (e) {
                console.error('解析响应失败', e);
                reject();
              }
            }
          };
        });
      } catch (error) {
        console.error('调用textin去屏幕纹API失败:', error);
        message.error('图片处理失败，请稍后重试');
        return null;
      }
    },
    [recognitTaskId],
  );

  /**
   * 去屏幕纹
   * @param {string} url 图片URL
   */
  const httpDemoireImageReq = useCallback(
    async (demoireData: {
      url: string;
      file: CompressFilesProps;
      md5?: string;
      taskId?: string;
      add?: boolean;
    }) => {
      try {
        const response = await callTextinDemoireAPIByUrl({
          url: isDev
            ? 'https://api.textin.com/ai/service/v1/demoire'
            : `${window.location.origin}/textin/ai/service/v1/demoire`,
          imageUrl: demoireData.url,
          taskId: demoireData.taskId,
        });

        // 将base64转换为ArrayBuffer
        const arrayBuffer = base64ToArrayBuffer(response as string);

        // 修边矫正
        httpCropEnhanceImageReq({
          url: arrayBuffer,
          file: demoireData.file,
          taskId: demoireData.taskId,
          add: demoireData.add,
        });
      } catch (error) {
        handleSetProcessingFiles({ ...demoireData.file }, true);
        message.error('去屏幕纹处理失败');
        // throw new Error('去屏幕纹处理失败');
        console.log(error);
      }
    },
    [],
  );

  /**
   * 修边矫正
   * @param {string} url 图片URL
   */
  const httpCropEnhanceImageReq = useCallback(
    async (cropData: {
      url: ArrayBuffer;
      file: CompressFilesProps;
      md5?: string;
      taskId?: string;
      add?: boolean;
    }) => {
      try {
        const response: any = await callTextinDemoireAPIByUrl({
          url: isDev
            ? 'https://api.textin.com/ai/service/v1/crop_enhance_image'
            : `${window.location.origin}/textin/ai/service/v1/crop_enhance_image`,
          imageUrl: cropData.url,
          taskId: cropData.taskId,
        });

        // console.log(response);

        if (cropData.add) {
          // 更新文件状态，身份脱敏中
          handleSetProcessingFiles({ ...cropData.file, uploadStatus: 'desensitization' });
        }

        // 保存图像
        httpSaveImageReq({
          url: response?.image_list[0]?.image,
          file: cropData.file,
          taskId: cropData.taskId,
          add: cropData.add,
        });
      } catch (error) {
        handleSetProcessingFiles({ ...cropData.file }, true);
        message.error('修边矫正处理失败');
        console.log(error);
      }
    },
    [],
  );

  /** 更新图片处理失败 */
  const httpImageProcessingFailReq = async (failData: { taskId?: string; failReason: string }) => {
    try {
      const res: any = await httpImageProcessingFail({
        taskId: failData.taskId,
        failReason: failData.failReason,
      });
      console.log(res);
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * 保存图像
   * @param {string} url 图片URL
   */
  const httpSaveImageReq = async (saveData: {
    url: string;
    file: CompressFilesProps;
    md5?: string;
    taskId?: string;
    add?: boolean;
  }) => {
    try {
      // 使用ref中的recognitTaskId，确保能获取到最新值
      const currentRecognitTaskId = recognitTaskIdRef.current;
      if (!currentRecognitTaskId) {
        console.error('recognitTaskId未设置，无法保存图像');
        handleSetProcessingFiles({ ...saveData.file }, true);
        return;
      }

      const res: any = await httpSaveImageClassify({
        recognitionTaskId: currentRecognitTaskId,
        preProcessBase64: saveData.url,
        taskId: saveData.taskId,
      });
      console.log(res);
      // 保存成功后
      if (res) {
        // 添加任务ID
        taskIdsRef.current.push(res);
        // 获取图片任务列表
        await httpImageProcessQueryPatientTaskReq(saveData.file);
        handleSetProcessingFiles({ ...saveData.file }, true);
      }
    } catch (error) {
      console.log(error);
      handleSetProcessingFiles({ ...saveData.file }, true);
    }
  };

  /** 获取患者当前已有的智能识别任务 */
  const httpImageRecognitionFindReq = async () => {
    try {
      const res: any = await httpImageRecognitionFind({
        patientId,
        taskType: scene,
        moduleType: fileType || undefined,
        recordId,
      });
      // console.log('获取患者当前已有的智能识别任务', res);
      if (!res) {
        httpImageRecognitionCreateReq();
      } else {
        setRecognitTaskId(res?.id);
        recognitTaskIdRef.current = res?.id; // 同时更新ref
      }
    } catch (error) {
      console.log(error);
    }
  };

  /** 创建图片智能识别任务 */
  const httpImageRecognitionCreateReq = async () => {
    try {
      const res: any = await httpImageRecognitionCreate({
        patientId,
        taskType: scene,
        moduleType: fileType || undefined,
        recordId,
      });
      // console.log('创建图片智能识别任务', res);
      setRecognitTaskId(res);
      recognitTaskIdRef.current = res; // 同时更新ref
    } catch (error) {
      console.log(error);
    }
  };

  /** 查询患者图片处理任务编号 */
  const httpImageProcessQueryPatientTaskReq = async (file?: CompressFilesProps) => {
    try {
      const res: any = await httpImageProcessQueryPatientTask({
        patientId,
        taskType: scene,
        statusList: ['in_processing'],
        fileType,
      });
      const { taskList = [] } = res || {};
      // console.log({ res, taskIds: taskIdsRef.current });

      if (taskIdsRef.current.length > 0 && taskList.length !== taskIdsRef.current.length) {
        const ids = taskList?.map((item: any) => item.id) || [];
        const completedFiles = taskIdsRef.current.filter((el: any) => !ids.includes(el));

        if (!completedFiles.length) return;

        // 调用接口查询任务详情
        completedFiles.forEach((item: any) => {
          httpImageProcessQueryTaskDetailsReq({ taskId: item });
        });

        taskIdsRef.current = completedFiles.length
          ? taskIdsRef.current.filter((el: any) => !completedFiles.includes(el))
          : [];
        // console.log({ taskIds: taskIdsRef.current });

        if (taskIdsRef.current.length === 0) {
          canOcr?.(true);
        }
      }

      if (taskList.length === 0) {
        clearInterval(intervalRef.current);
      } else {
        clearInterval(intervalRef.current);
        interFn();
        taskIdsRef.current = taskList.map((el: any) => el.id);
        canOcr?.(false);
      }
      // 过滤视频任务
      const fileUnit = ['mp4', 'mov'];
      const filterTaskList = taskList?.filter(
        (el: any) => !fileUnit.includes(el.beforeFilePath.split('.').pop()),
      );
      if (filterTaskList.length) {
        setPatientImageTasks(filterTaskList);
      } else {
        setPatientImageTasks([]);
        clearInterval(intervalRef.current);
      }
    } catch (err) {
      console.log(err);
      clearInterval(intervalRef.current);
    }
  };

  /** 轮询图片任务列表 */
  const interFn = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    intervalRef.current = setInterval(async () => {
      await httpImageProcessQueryPatientTaskReq();
    }, 2000);
  };

  /** 查询图片处理任务详情 */
  const httpImageProcessQueryTaskDetailsReq = async (task: {
    taskId: string;
    file?: CompressFilesProps;
  }) => {
    try {
      const res: any = await httpImageProcessQueryTaskDetails({ taskId: task.taskId });
      const { status } = res;
      if (status !== 'in_processing') {
        if (status === 'completed') {
          message.success('图片处理成功');
        }
        if (status === 'unqualified') {
          message.error('图片不清晰，请重新上传');
        }
        if (status === 'fail') {
          message.error(res?.failReason || '图片处理失败');
        }
        // 单模块上传，上传成功后，返回图片数据
        if (scene === 'single' && callback) {
          callback(res?.afterFilePath ? [res?.afterFilePath] : []);
        }
      }
      if (status !== 'in_processing' && task.file) {
        handleSetProcessingFiles({ ...task.file }, true);
      }
      // 多模块上传，上传成功后，调用回调
      if (callback && scene === 'multi') {
        callback();
      }
    } catch (error) {
      console.log(error);
    }
  };

  /** 统一管理处理中的文件状态 */
  const handleSetProcessingFiles = (file: ProcessingFilesProps, remove: boolean = false) => {
    if (remove) {
      const process = [...processingImageFiles];
      const newProcess = process.filter((el) => el.id !== file.id);
      setProcessingImageFiles(newProcess);
      if (newProcess.length === 0) {
        setSaveImageLoading(false);
      }
      return;
    }

    setProcessingImageFiles((prev) => {
      const existingIndex = prev.findIndex((el) => el.id === file.id);
      if (existingIndex >= 0) {
        // 更新现有文件的状态
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], ...file };
        return updated;
      }
      // 添加新文件
      return [...prev, file];
    });
  };

  /** 分批处理文件上传，每批最多5个文件 */
  const processFilesInBatches = async (files: CompressFilesProps[]) => {
    const BATCH_SIZE = 5; // 每批最多同时上传5个文件
    const newFiles = files.filter((file) => file.new);

    // 初始化未上传文件列表
    setProcessingImageFiles((prev) => {
      return [...prev, ...newFiles.map((file) => ({ ...file, uploadStatus: 'waiting' as const }))];
    });

    // 通过MD5过滤已存在的文件
    const filteredFiles = await Promise.all(
      newFiles.map(async (file) => {
        // 计算文件MD5
        const fileMD5 = await calculateFileMD5(file.file);
        try {
          // 调用接口检查文件是否已存在
          const response: any = await httpGetImageByMd5({
            recognitionTaskId: recognitTaskIdRef.current, // 使用ref中的值
            fileMd5: fileMD5,
            patientId,
            scene,
            fileType,
          });

          // 如果接口返回数据，说明文件已存在，过滤掉
          if (response) {
            // console.log(`文件 ${file.file.name} 已存在，MD5: ${fileMD5}`);
            httpImageProcessQueryTaskDetailsReq({ taskId: response, file });
            return null; // 返回null表示需要过滤掉
          }

          // 文件不存在，保留
          return { file, md5: fileMD5 };
        } catch (error) {
          // console.error(`计算文件MD5失败: ${file.file.name}`, error);
          // 出错时保留文件，避免误删
          handleSetProcessingFiles({ ...file }, true);
          return null;
        }
      }),
    );

    // console.log('filteredFiles', { filteredFiles });

    // 过滤掉null值（已存在的文件）
    const residueFiles = filteredFiles.filter((file) => file !== null) as {
      file: CompressFilesProps;
      md5: string;
    }[];

    console.log('开始并发上传所有图片', {
      totalFiles: newFiles.length,
      residueFiles,
    });

    if (residueFiles.length === 0) {
      setSaveImageLoading(false);
      return;
    }

    // 并发上传所有图片，一旦有5张上传成功就立即处理
    const uploadPromises = residueFiles.map((item) => handleCosUploadFile(item.file, item.md5));

    // 批次计数
    let batchCount = 0;

    // 创建Promise.race来监听上传完成
    const processBatch = async (uploadResults: any[]) => {
      // 批次计数
      batchCount += 1;
      // console.log(`处理第 ${batchCount} 批，共 ${uploadResults.length} 个文件`);

      try {
        const filesToKey = uploadResults.map((el) => {
          return {
            key: el.processedLocation,
            file: el.file,
            md5: el.md5,
          };
        });

        // 调用图像分类接口
        await httpImageClassifyReq(filesToKey);
        // console.log(`第 ${batchCount} 批图像分类处理完成`);
      } catch (error) {
        console.error(`第 ${batchCount} 批处理失败:`, error);
        message.error(`第 ${batchCount} 批图片处理失败，请稍后重试`);
      }
    };

    // 监听上传完成，一旦有5张就立即处理
    const cosResults: any[] = [];
    const uploadPromisesWithCallback = uploadPromises.map((promise, index) =>
      promise.then((result) => {
        cosResults.push(result);

        // 一旦有5张上传成功，立即处理
        if (cosResults.length >= BATCH_SIZE) {
          const batch = cosResults.splice(0, BATCH_SIZE);
          processBatch(batch);
        }

        return result;
      }),
    );

    // 等待所有上传完成
    await Promise.all(uploadPromisesWithCallback);

    // 处理剩余的上传结果（不足5张的部分）
    if (cosResults.length > 0) {
      // console.log(`处理剩余 ${cosResults.length} 张图片`);
      await processBatch(cosResults);
    }

    // console.log('所有批次处理完成');
  };

  useEffect(() => {
    if (uploadImageFiles.length) {
      if (!recognitTaskIdRef.current) {
        httpImageRecognitionFindReq();
        return;
      }

      setSaveImageLoading(true);
      canOcr?.(false);
      const allFiles = [...uploadImageFiles];
      console.log('开始上传之前', {
        allFiles,
        uploadImageFiles,
        recognitTaskId: recognitTaskIdRef.current,
      });

      // 使用队列上传替代并发上传
      processFilesInBatches(allFiles)
        .then(() => {
          console.log('所有批次上传完成');
        })
        .catch((error) => {
          console.error('文件上传过程中发生错误:', error);
        })
        .finally(() => {
          setUploadImageFiles([]);
          // console.log('上传流程结束');
        });
    }
  }, [uploadImageFiles, recognitTaskId]);

  useEffect(() => {
    if (!fileType) {
      httpImageProcessQueryPatientTaskReq();
    }

    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [fileType]);

  return {
    uploadImageFiles,
    processingImageFiles,
    setProcessingImageFiles,
    setUploadImageFiles,
    patientImageTasks,
    saveImageLoading,
    recognitTaskId,
    stareDemoireImage,
  };
};

/**
 * 初始化COS
 */
export const useInitCos = () => {
  // 临时key
  const tempCosKeyRef = useRef<any>(null);
  // COS实例
  const cosInstanceRef = useRef<any>(null);
  useEffect(() => {
    tempCosKeyRef.current = getLocalStorage('cosTempKey');

    handleInitCos();
  }, []);

  /**
   * 初始化COS
   */
  const handleInitCos = useCallback(async () => {
    if (!isCOSKeyValid(tempCosKeyRef?.current)) {
      // console.log('cos临时密钥有效');
      const tempKey: any = await httpCosCredentials();
      setLocalStorage('cosTempKey', tempKey);
      tempCosKeyRef.current = tempKey;
    }
    if (cosInstanceRef?.current) return;
    try {
      const cos = new COS({
        getAuthorization: (options: any, fn: any) => {
          fn({
            TmpSecretId: tempCosKeyRef?.current?.accessKeyId,
            TmpSecretKey: tempCosKeyRef?.current?.accessKeySecret,
            SecurityToken: tempCosKeyRef?.current?.securityToken,
            StartTime: Math.floor(Date.now() / 1000), // 确保是10位整数时间戳
            ExpiredTime: tempCosKeyRef?.current?.expiration || 0,
          });
        },
      });

      cosInstanceRef.current = cos;
    } catch (error) {
      console.log(error);
    }
  }, []);

  /**
   * 上传文件到COS
   * @param {File} file 文件
   */
  const handleCosUploadFile = useCallback(
    async (
      file: CompressFilesProps,
      md5: string,
      callback?: (f: ProcessingFilesProps, flag?: boolean) => void,
    ) => {
      if (!cosInstanceRef.current) {
        await handleInitCos();
      }
      // 生成文件名
      const cosKey = generateFilename(file.file.name);

      // 生成路径
      const path = file.file.type.includes('image') ? 'var' : 'video';

      callback?.({ ...file, uploadStatus: 'uploading', new: false }, false);
      // 上传文件
      return new Promise((resolve, reject) => {
        // 上传文件到COS
        cosInstanceRef.current?.putObject(
          {
            Bucket: tempCosKeyRef?.current?.bucketName, // 桶名
            Region: tempCosKeyRef?.current?.region, // 区域
            Key: `${path}/${cosKey}`, // 文件名
            SliceSize: 1024 * 1024 * 5, // 触发分块上传的阈值，超过5MB使用分块上传，小于5MB使用简单上传。可自行设置，非必须
            Body: file.file, // 文件
            onProgress: (data: any) => {
              // console.log(data);
            },
          },
          (err: any, data: any) => {
            if (err) {
              // console.log(JSON.stringify(err));
              // 移除图片记录
              message.error('cos上传失败，请稍后重试');
              callback?.({ ...file, uploadStatus: 'fail' }, true);
              reject(err);
            } else {
              // console.log('上传成功', data);
              // 处理Location，提取出路径后面的文件名
              const processedLocation = processCOSLocation(data.Location);
              callback?.(file, false);
              resolve({
                processedLocation, // 添加处理后的Location
                originalLocation: data.Location, // 保留原始Location
                file, // 文件
                md5, // 文件MD5
              });
            }
          },
        );
      });
    },
    [],
  );

  return {
    cosInstanceRef,
    tempCosKeyRef,
    uploadFileToCos: handleCosUploadFile,
  };
};

interface ProcessingVideoFilesProps extends CompressFilesProps {
  uploadStatus?: 'waiting' | 'uploading' | 'processing';
}

/**
 * 视频上传
 * @param {Function} callback 上传完成回调
 * @returns {Object} 返回视频上传状态、文件列表、处理中的文件列表
 */
export const useVideoUpload = (callback?: (urls?: string[]) => void) => {
  const [videoUploading, setVideoUploading] = useState(false); // 视频上传中
  const [videoUploadFiles, setVideoUploadFiles] = useState<CompressFilesProps[]>([]); // 视频文件
  const [processingVideoFiles, setProcessingVideoFiles] = useState<ProcessingVideoFilesProps[]>([]); // 处理中的视频文件

  /**
   * 分批处理视频上传，每批最多5个文件
   * @param {Array<File>} files 视频文件数组
   */
  const processVideoFilesInBatches = async (files: CompressFilesProps[]) => {
    const BATCH_SIZE = 5; // 每批最多同时上传5个文件

    // 初始化处理中的文件列表
    setProcessingVideoFiles(files.map((file) => ({ ...file, uploadStatus: 'waiting' as const })));

    console.log('开始分批上传视频文件', {
      totalFiles: files.length,
      batchSize: BATCH_SIZE,
    });

    // 创建所有批次
    const batches: CompressFilesProps[][] = [];
    for (let i = 0; i < files.length; i += BATCH_SIZE) {
      batches.push(files.slice(i, i + BATCH_SIZE));
    }

    // 处理单个批次的函数
    const processBatch = async (batch: CompressFilesProps[], batchIndex: number) => {
      // console.log(`处理第 ${batchIndex + 1} 批，共 ${batch.length} 个文件`);

      try {
        // 并发上传当前批次的所有文件
        const uploadPromises = batch.map((file) => {
          const formData = new FormData();
          formData.append('file', file.file);
          return uploadFile(formData).then((res: any) => ({
            file,
            result: res,
          }));
        });

        // 等待当前批次所有文件上传完成
        const batchResults = await Promise.all(uploadPromises);
        // console.log(`第 ${batchIndex + 1} 批视频上传完成:`, batchResults);

        // 从处理中的文件列表中移除已完成的文件
        setProcessingVideoFiles((prev) =>
          prev.filter((file) => !batch.some((batchFile) => batchFile.id === file.id)),
        );
        callback?.(batchResults.map((el) => el.result));
        return batchResults;
      } catch (error) {
        // console.error(`第 ${batchIndex + 1} 批视频上传失败:`, error);
        // message.error(`第 ${batchIndex + 1} 批视频上传失败，请稍后重试`);

        // 从处理中的文件列表中移除失败的文件
        setProcessingVideoFiles((prev) =>
          prev.filter((file) => !batch.some((batchFile) => batchFile.id === file.id)),
        );

        throw error;
      }
    };

    // 递归处理批次
    const processBatchesSequentially = async (index: number): Promise<void> => {
      if (index >= batches.length) return;

      const batch = batches[index];
      await processBatch(batch, index);

      // 批次间延迟1秒，避免服务器压力过大
      if (index < batches.length - 1) {
        await new Promise<void>((resolve) => {
          setTimeout(() => resolve(), 1000);
        });
      }

      await processBatchesSequentially(index + 1);
    };

    await processBatchesSequentially(0);

    // console.log('所有批次视频上传完成');
  };

  // 上传视频
  const handleUploadVideo = async (videoFiles: CompressFilesProps[]) => {
    setVideoUploading(true);

    if (!videoFiles.length) {
      setVideoUploading(false);
      return [];
    }

    try {
      // 使用分批处理方式上传文件
      await processVideoFilesInBatches(videoFiles);

      // 清空上传文件列表
      setVideoUploadFiles([]);
      setProcessingVideoFiles([]);

      return videoFiles.map(() => ({ success: true })); // 返回成功结果
    } catch (error) {
      console.error('视频上传过程中发生错误:', error);
      message.error('视频上传失败，请稍后重试');
      throw error;
    } finally {
      setVideoUploading(false);
    }
  };

  useEffect(() => {
    if (videoUploadFiles.length) {
      handleUploadVideo(videoUploadFiles);
    }
  }, [videoUploadFiles]);

  return {
    videoUploading,
    videoUploadFiles,
    setVideoUploadFiles,
    processingVideoFiles, // 新增：返回处理中的文件列表
  };
};
